<template>
  <template v-if="loading">
    <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
  </template>
  <template v-else>
    <InboxEmpty v-if="emptyComms" />
    <div v-if="hasUnread" class="tw-flex tw-flex-col tw-pb-4 tw-px-4">
      <div
        class="
          tw-h-16
          tw-sticky
          tw-top-0
          tw-bg-sf-snow-lighter
          tw-z-50
          tw-flex
          tw-flex-row
          tw-items-end
        "
      >
        <span
          class="
            tw-font-medium
            tw-text-lg
            tw-py-2
            tw-flex
            tw-flex-row
            tw-w-full
            tw-items-baseline
            tw-justify-between
          "
        >
          <span>{{ I18n.t('communication.inbox.unread_messages') }}</span>
          <span>
            <span
              class="
                tw-uppercase
                tw-font-medium
                tw-text-xs
                tw-text-sf-gray-dark
                tw-p-2
              "
            >
              {{ totalUnreadMessagesCopy }}
            </span>
            <SfButtonInverted
              small
              inline
              variant="dark"
              :disabled="markingAsRead"
              @click="markAllAsRead"
            >
              <span
                class="
                  tw-flex tw-flex-row tw-items-center tw-align-baseline tw-gap-2
                "
              >
                <i class="fa fa-envelope-open" />
                {{ I18n.t('communication.inbox.mark_all_as_read') }}
              </span>
            </SfButtonInverted>
          </span>
        </span>
      </div>
      <div class="tw-gap-4 tw-flex tw-flex-col">
        <RemarkGroup
          v-if="!!groupedUnread['Company']"
          :name="I18n.t('company.start')"
          show-mark-as-read
          :disable-mark-as-read="markingAsRead"
          @mark-as-read="markCategoryAsRead('Company')"
        >
          <template
            v-for="comm in groupedUnread['Company']['Client overview']"
            :key="`${comm.kind}-${comm.id}`"
          >
            <RemarkCard
              v-if="comm.kind === 'remark-card'"
              :remark="comm"
              show-context-btn
              show-period
              show-tags
              part-of="inbox"
            />
            <NotificationContent
              v-else
              type="CheckList"
              :communication-item="comm"
            />
          </template>
          <template #message-count-indicator>
            {{
              I18n.t('communication.inbox.messages', {
                count: groupedUnread['Company']['Client overview'].length,
              })
            }}
          </template>
        </RemarkGroup>
        <RemarkGroup
          v-if="!!groupedUnread['ClientMeeting']"
          :name="I18n.t('client_meetings.title')"
          show-mark-as-read
          :disable-mark-as-read="markingAsRead"
          @mark-as-read="markCategoryAsRead('ClientMeeting')"
        >
          <template
            v-for="clientMeeting in Object.keys(groupedUnread['ClientMeeting'])"
            :key="clientMeeting"
          >
            <template
              v-for="comm in groupedUnread['ClientMeeting'][clientMeeting]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkCard
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
        </RemarkGroup>
        <RemarkGroup
          v-for="section in unreadSections"
          :key="section.categoryId"
          :name="section.categoryName"
          show-mark-as-read
          :disable-mark-as-read="markingAsRead"
          @mark-as-read="markCategoryAsRead(section.categoryId)"
        >
          <template
            v-for="item in Object.keys(groupedUnread[section.categoryId])"
            :key="`${section.categoryName}-${item}`"
          >
            <template
              v-for="comm in groupedUnread[section.categoryId][item]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkCard
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-context
                show-period
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
          <template #message-count-indicator>
            {{
              I18n.t('communication.inbox.messages', {
                count: section.count,
              })
            }}
          </template>
        </RemarkGroup>
        <div
          class="
            tw-mx-auto
            tw-my-4
            tw-font-medium
            tw-text-sm
            tw-text-sf-gray-light
            tw-text-center
          "
        >
          <SfButtonInverted
            small
            inline
            variant="dark"
            :disabled="markingAsRead"
            @click="markAllAsRead"
          >
            <span
              data-testid="firm-inbox-mark-all-as-read"
              class="
                tw-flex tw-flex-row tw-items-center tw-align-baseline tw-gap-2
              "
            >
              <i class="fa fa-envelope-open" />
              {{ I18n.t('communication.inbox.mark_all_as_read') }}
            </span>
          </SfButtonInverted>
        </div>
      </div>
    </div>
    <div v-if="hasRead" class="tw-flex tw-flex-col tw-pb-4 tw-px-4">
      <div
        class="
          tw-h-16
          tw-sticky
          tw-top-0
          tw-bg-sf-snow-lighter
          tw-z-50
          tw-flex
          tw-flex-row
          tw-items-end
        "
      >
        <span
          class="
            tw-font-medium
            tw-text-lg
            tw-py-2
            tw-flex
            tw-flex-row
            tw-w-full
            tw-items-baseline
            tw-justify-between
          "
        >
          {{ I18n.t('communication.inbox.read_messages') }}
        </span>
      </div>
      <div class="tw-gap-4 tw-flex tw-flex-col">
        <RemarkGroup
          v-if="!!groupedRead['Company']"
          :name="I18n.t('company.start')"
          :show-mark-as-read="false"
          disable-mark-as-read
        >
          <template
            v-for="comm in groupedRead['Company']['Client overview']"
            :key="`${comm.kind}-${comm.id}`"
          >
            <RemarkCard
              v-if="comm.kind === 'remark-card'"
              :remark="comm"
              show-context-btn
              show-context
              show-period
              show-tags
              part-of="inbox"
            />
            <NotificationContent
              v-else
              type="CheckList"
              :communication-item="comm"
            />
          </template>
          <template #message-count-indicator>
            {{
              I18n.t('communication.inbox.messages', {
                count: groupedRead['Company']['Client overview'].length,
              })
            }}
          </template>
        </RemarkGroup>
        <RemarkGroup
          v-if="!!groupedRead['ClientMeeting']"
          :name="I18n.t('client_meetings.title')"
        >
          <template
            v-for="clientMeeting in Object.keys(groupedRead['ClientMeeting'])"
            :key="clientMeeting"
          >
            <template
              v-for="comm in groupedRead['ClientMeeting'][clientMeeting]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkCard
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
        </RemarkGroup>
        <RemarkGroup
          v-for="section in readSections"
          :key="section.categoryId"
          :name="section.categoryName"
          :show-mark-as-read="false"
          disable-mark-as-read
        >
          <template
            v-for="item in Object.keys(groupedRead[section.categoryId])"
            :key="`${section.categoryName}-${item}`"
          >
            <template
              v-for="comm in groupedRead[section.categoryId][item]"
              :key="`${comm.kind}-${comm.id}`"
            >
              <RemarkCard
                v-if="comm.kind === 'remark-card'"
                :remark="comm"
                show-context-btn
                show-context
                show-period
                show-tags
                part-of="inbox"
              />
              <NotificationContent
                v-else
                type="CheckList"
                :communication-item="comm"
              />
            </template>
          </template>
          <template #message-count-indicator>
            {{
              I18n.t('communication.inbox.messages', {
                count: section.count,
              })
            }}
          </template>
        </RemarkGroup>
      </div>
      <div
        class="
          tw-mx-auto
          tw-my-4
          tw-font-medium
          tw-text-sm
          tw-text-sf-gray-light
          tw-text-center
        "
      ></div>
    </div>
  </template>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed, onBeforeUnmount, onMounted } from 'vue'

import RemarkGroup from '@app/application/communication/screens/inbox/components/remark-group.vue'
import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import NotificationContent from '@app/application/communication/screens/inbox/components/notification-content.vue'
import SfButtonInverted from '../../../../../shared/vue/components/sf-button/sf-button-inverted.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'
import InboxEmpty from '@app/application/communication/screens/inbox/components/inbox-empty.vue'

const store = useStore()
const I18n = useI18n()

const {
  groupedUnread,
  groupedRead,
  groupedCommunication,
  markAllAsRead,
  markCategoryAsRead,
} = useGroupedFilteredCommunications()

const markingAsRead = computed(() => store.state.sections.markingAsRead)

const emptyComms = computed(() => {
  return Object.keys(groupedCommunication.value).length === 0
})

const emptyNotifications = computed(() => {
  return Object.keys(store.state.notifications.notifications).length === 0
})

const totalUnreadMessagesCopy = computed(() => {
  const totalUnread =
    unreadSections.value.reduce((acc, item) => acc + item.count, 0) +
    (groupedUnread.value['Company']?.['Client overview']?.length || 0)
  return I18n.t('communication.inbox.messages', { count: totalUnread })
})

const loading = computed(
  () =>
    emptyNotifications.value ||
    store.getters.loadingWorkflows ||
    store.getters.loadingMentionUsernames ||
    (emptyComms.value && store.getters.fetchingCommunicationsInProgress)
)

const hasUnread = computed(() => Object.keys(groupedUnread.value).length > 0)

const hasRead = computed(() => Object.keys(groupedRead.value).length > 0)

const unreadSections = computed(() => {
  const result = []
  for (const section of store.getters.sections) {
    if (groupedUnread.value[section.categoryId]) {
      const count = Object.values(
        groupedUnread.value[section.categoryId] || {}
      ).reduce((acc, item) => acc + item.length, 0)
      result.push({ ...section, count })
    }
  }
  return result
})

const readSections = computed(() => {
  const result = []
  for (const section of store.getters.sections) {
    if (groupedRead.value[section.categoryId]) {
      const count = Object.values(
        groupedRead.value[section.categoryId] || {}
      ).reduce((acc, item) => acc + item.length, 0)
      result.push({ ...section, count })
    }
  }
  return result
})

function refreshCommunicationData() {
  store.dispatch('fetchCommunication')
}

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>

<style scoped>
.remark-group {
  --sf-remark-group-sticky-offset: 64px;
}
</style>
